import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Clock, Sparkles } from 'lucide-react';
import BlobButton from './BlobButton';
import { submitLeadWithGeo } from '../services/apiService';
import { useGeolocation } from '../hooks/useGeolocation';

const Hero = () => {
  const { country, isLoading: geoLoading, error: geoError, retry: retryGeo } = useGeolocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Countdown timer logic (same as DiscountBanner)
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  const ONE_HOUR = 60 * 60 * 1000;

  const getInitialEndTime = () => {
    const stored = localStorage.getItem('discountEndTime');
    if (stored) {
      return parseInt(stored);
    }
    const endTime = Date.now() + SEVEN_DAYS;
    localStorage.setItem('discountEndTime', endTime.toString());
    return endTime;
  };

  const [endTime, setEndTime] = useState(getInitialEndTime());
  const [timeLeft, setTimeLeft] = useState(endTime - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = endTime - Date.now();
      
      if (remaining <= ONE_HOUR && remaining > 0) {
        const newEndTime = Date.now() + SEVEN_DAYS;
        setEndTime(newEndTime);
        localStorage.setItem('discountEndTime', newEndTime.toString());
        setTimeLeft(SEVEN_DAYS);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  const formatTime = (ms) => {
    if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);
    
    return { days, hours, minutes, seconds };
  };

  const time = formatTime(timeLeft);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[0-9]{10,}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if geolocation is still loading
    if (geoLoading) {
      alert('Please wait while we detect your location...');
      return;
    }
    
    // Check if geolocation failed
    if (geoError) {
      alert('Unable to detect your location. Please check your internet connection and try again.');
      return;
    }
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        // Submit lead with geolocation (geolocation is mandatory)
        const result = await submitLeadWithGeo({
          fullName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          message: formData.message
        });

        if (!result.success) {
          // Show error message from API
          alert(result.message || 'Something went wrong. Please try again.');
        }
        // Success redirect handled in API service
      } catch (error) {
        console.error('Form submission error:', error);
        alert('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  return (
    <div className="relative min-h-screen lg:h-screen bg-white pt-20">
      <div className="container mx-auto px-4 py-4 md:py-6 lg:py-8 xl:py-12 h-full">
        <div className="grid lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 xl:gap-12 items-center h-full">
          {/* Left Side */}
          <div className="flex flex-col justify-center">
            {/* USP Section */}
            <div className="mb-4 md:mb-6">
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-6">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-amber-700 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-gray-600 text-sm md:text-base lg:text-lg">Why choose us:</span>
              </div>
              
              <div className="flex flex-wrap gap-2 md:gap-4 max-w-2xl">
                <div className="px-3 py-1.5 md:px-6 md:py-3 border-2 border-gray-300 rounded-full text-gray-700 text-xs md:text-sm lg:text-base font-medium hover:border-amber-700 hover:text-amber-700 transition-colors cursor-default whitespace-nowrap">
                  Fast Turnaround
                </div>
                <div className="px-3 py-1.5 md:px-6 md:py-3 border-2 border-gray-300 rounded-full text-gray-700 text-xs md:text-sm lg:text-base font-medium hover:border-amber-700 hover:text-amber-700 transition-colors cursor-default whitespace-nowrap">
                  Professional Editing
                </div>
                <div className="px-3 py-1.5 md:px-6 md:py-3 border-2 border-gray-300 rounded-full text-gray-700 text-xs md:text-sm lg:text-base font-medium hover:border-amber-700 hover:text-amber-700 transition-colors cursor-default whitespace-nowrap">
                  Global Distribution
                </div>
                <div className="px-3 py-1.5 md:px-6 md:py-3 border-2 border-gray-300 rounded-full text-gray-700 text-xs md:text-sm lg:text-base font-medium hover:border-amber-700 hover:text-amber-700 transition-colors cursor-default whitespace-nowrap">
                  Award-Winning Design
                </div>
              </div>
            </div>

            {/* Main Headline - SEO Optimized */}
            <div className="relative mt-4 md:mt-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-gray-900">Publish Your Book</span><br className="hidden sm:block" />
                <span className="text-gray-900"> Online & On</span><br />
                <span className="text-amber-700">Amazon</span>
              </h1>
              <p className="text-base md:text-lg text-gray-600 mt-4 max-w-xl">
                Professional book publishing services to help you write and publish a book. Publish your own book with expert support from manuscript to bestseller.
              </p>
              
              {/* Chat Button */}
              <div className="mt-6">
                <BlobButton 
                  variant="default"
                  className="open-chat js-chat whitespace-nowrap"
                >
                  <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Discuss Your Book
                </BlobButton>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form with Book Image */}
          <div className="relative flex justify-end items-start">
            {/* Book Image - hidden on mobile, shown on larger screens, attached to form left edge */}
            <div className="hidden lg:block absolute left-0 top-0 z-0 pointer-events-none">
              <style>{`
                @keyframes bookReveal {
                  0% {
                    transform: rotate(35deg) translateX(180px) translateY(20px);
                    opacity: 0;
                  }
                  100% {
                    transform: rotate(-8deg) translateY(20px);
                    opacity: 0.95;
                  }
                }
              `}</style>
              <img 
                src="https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/r3p0r1mn_header%202.png"
                alt="Decorative Book"
                className="w-80 h-auto object-contain opacity-95"
                style={{ 
                  filter: 'drop-shadow(-15px 25px 35px rgba(0, 0, 0, 0.4))',
                  transform: 'rotate(-8deg) translateY(20px)',
                  animation: 'bookReveal 1.5s ease-out forwards'
                }}
              />
            </div>

            {/* Form Card */}
            <div className="w-full max-w-md rounded-xl md:rounded-2xl p-4 sm:p-5 lg:p-6 shadow-2xl bg-white relative z-10" style={{ backgroundColor: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
              {/* Discount Badge */}
              <div className="flex items-center justify-center gap-2 mb-2 lg:mb-3">
                <Sparkles className="w-4 h-4 text-amber-700" />
                <span className="text-amber-700 font-bold text-sm lg:text-base">40% OFF First-Time Orders!</span>
              </div>
              
              {/* Form Heading */}
              <div className="text-center mb-3 lg:mb-4">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
                  Get Started Today<br className="hidden sm:block" />
                  <span className="text-amber-700"> Publish Your Book</span>
                </h2>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3 lg:space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.name}</p>}
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.email}</p>}
                </div>

                {/* Country Field - Auto-detected (MANDATORY) */}
                <div className="relative">
                  <input
                    type="text"
                    name="country"
                    placeholder={geoLoading ? "Detecting country..." : geoError ? "Location detection failed" : "Country"}
                    value={geoError ? '' : country}
                    disabled
                    className={`w-full bg-gray-50 border-0 border-b-2 ${geoError ? 'border-red-300' : 'border-gray-300'} outline-none px-0 py-3 text-gray-700 placeholder:text-gray-500 cursor-not-allowed`}
                  />
                  {geoError && (
                    <button
                      type="button"
                      onClick={retryGeo}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-amber-700 hover:text-amber-800 font-semibold"
                    >
                      Retry
                    </button>
                  )}
                  {geoError && <p className="text-red-500 text-xs mt-1">Location is required. Click retry.</p>}
                </div>

                <div className="relative">
                  <div className="flex gap-3">
                    <select
                      className="bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none py-3 text-gray-700 transition-colors cursor-pointer"
                      defaultValue="+1"
                    >
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+86">🇨🇳 +86</option>
                      <option value="+81">🇯🇵 +81</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+34">🇪🇸 +34</option>
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="flex-1 bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.phone}</p>}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    name="message"
                    placeholder="Project Details"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-3 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                  {errors.message && <p className="text-red-500 text-sm mt-1 absolute -bottom-5">{errors.message}</p>}
                </div>

                {/* Countdown Timer */}
                <div className="flex flex-wrap items-center justify-center gap-2 text-gray-600 py-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-semibold">Offer expires in:</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-center">
                      <div className="bg-amber-700 rounded px-2 py-1">
                        <span className="text-sm font-bold text-white">{time.days}</span>
                      </div>
                      <span className="text-[10px] text-gray-600 block">Days</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 rounded px-2 py-1">
                        <span className="text-sm font-bold text-white">{time.hours}</span>
                      </div>
                      <span className="text-[10px] text-gray-600 block">Hours</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 rounded px-2 py-1">
                        <span className="text-sm font-bold text-white">{time.minutes}</span>
                      </div>
                      <span className="text-[10px] text-gray-600 block">Mins</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 rounded px-2 py-1">
                        <span className="text-sm font-bold text-white">{time.seconds}</span>
                      </div>
                      <span className="text-[10px] text-gray-600 block">Secs</span>
                    </div>
                  </div>
                </div>

                <BlobButton
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(e);
                  }}
                  disabled={geoLoading || geoError || isSubmitting}
                  className="w-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : geoLoading ? 'Detecting Location...' : 'Claim 40% OFF & Start Publishing'}
                </BlobButton>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent"></div>
    </div>
  );
};

export default Hero;